import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Autocomplete, type AutocompleteOption } from "@/components/ui/autocomplete";
import { useFocusTaxCodeSearch } from "@/hooks/useFocusTaxCodeSearch";
import type { FocusTaxCodeMode, FocusTaxCodeType } from "@/lib/fiscal/focus-tax-codes";

interface FocusTaxCodeAutocompleteProps {
  type: FocusTaxCodeType;
  mode?: FocusTaxCodeMode;
  value?: string | null;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
}

export function FocusTaxCodeAutocomplete({
  type,
  mode,
  value,
  onChange,
  placeholder,
  disabled,
}: FocusTaxCodeAutocompleteProps) {
  const { t } = useTranslation("erp");
  const [displayText, setDisplayText] = useState(value ?? "");
  const [pendingExactCode, setPendingExactCode] = useState("");
  const normalizedValue = value ?? "";
  const exactQuery = useFocusTaxCodeSearch({
    type,
    mode: "exact",
    query: normalizedValue,
    enabled: normalizedValue.length > 0,
  });
  const pendingExactQuery = useFocusTaxCodeSearch({
    type,
    mode: "exact",
    query: pendingExactCode,
    enabled: pendingExactCode.length > 0,
  });
  const searchQuery = useFocusTaxCodeSearch({
    type,
    mode,
    query: displayText,
    enabled: !disabled,
  });

  const options = useMemo<AutocompleteOption[]>(
    () => searchQuery.data?.options ?? [],
    [searchQuery.data?.options]
  );
  const exactOption = exactQuery.data?.options?.[0];

  useEffect(() => {
    if (exactOption && exactOption.value === normalizedValue) {
      setDisplayText(exactOption.label);
      return;
    }

    if (!normalizedValue) {
      setDisplayText("");
    }
  }, [exactOption, normalizedValue]);

  useEffect(() => {
    if (!pendingExactCode || pendingExactQuery.isFetching) return;

    const pendingOption = pendingExactQuery.data?.options?.find((option) => option.value === pendingExactCode);
    if (pendingOption) {
      setDisplayText(pendingOption.label);
      onChange(pendingOption.value);
    } else if (exactOption?.value === normalizedValue) {
      setDisplayText(exactOption.label);
    } else {
      setDisplayText(normalizedValue);
    }

    setPendingExactCode("");
  }, [
    exactOption,
    normalizedValue,
    onChange,
    pendingExactCode,
    pendingExactQuery.data?.options,
    pendingExactQuery.isFetching,
  ]);

  const isLoading = searchQuery.isFetching || exactQuery.isFetching || pendingExactQuery.isFetching;
  const expectedCodeLength = type === "ncm" ? 8 : 4;
  const codeTypeLabel = type.toUpperCase();
  const emptyMessage = isLoading ? (
    t("itemForm.fiscal.focusSearch.loading")
  ) : (
    <div className="space-y-1 px-3 py-2 text-left">
      <p className="font-medium text-foreground">
        {t("itemForm.fiscal.focusSearch.emptyTitle", { codeType: codeTypeLabel })}
      </p>
      <p className="text-muted-foreground">
        {t("itemForm.fiscal.focusSearch.emptyDescription", { codeType: codeTypeLabel })}
      </p>
      <p className="text-xs text-muted-foreground">
        {t(
          type === "cfop"
            ? "itemForm.fiscal.focusSearch.emptyCfopHint"
            : "itemForm.fiscal.focusSearch.emptyNcmHint"
        )}
      </p>
    </div>
  );

  return (
    <div className="relative">
      <Autocomplete
        options={options}
        value={displayText}
        selectedValue={normalizedValue}
        onChange={(nextValue) => {
          setDisplayText(nextValue);
          if (nextValue.trim() === "") {
            onChange("");
          }
        }}
        onSelect={(option) => {
          setDisplayText(option.label);
          onChange(option.value);
        }}
        onBlur={() => {
          const digits = displayText.replace(/\D/g, "");
          const hasExpectedLength = digits.length === expectedCodeLength;
          const hasExpectedCfopPrefix = type !== "cfop" || !mode || mode === "exact" || digits.startsWith(mode === "inter" ? "6" : "5");

          if (hasExpectedLength && hasExpectedCfopPrefix) {
            const matchingOption = options.find((option) => option.value === digits);
            if (matchingOption) {
              setDisplayText(matchingOption.label);
              onChange(matchingOption.value);
              return;
            }

            if (exactOption?.value === digits) {
              setDisplayText(exactOption.label);
              onChange(exactOption.value);
              return;
            }

            setPendingExactCode(digits);
            return;
          }

          if (exactOption?.value === normalizedValue) {
            setDisplayText(exactOption.label);
          } else {
            setDisplayText(normalizedValue);
          }
        }}
        placeholder={placeholder}
        emptyMessage={emptyMessage}
        disabled={disabled}
        className="pr-9"
        dropdownClassName="right-auto w-[min(28rem,calc(100vw-3rem))]"
        listClassName="max-h-[26rem]"
        optionClassName="py-2.5"
        disableLocalFilter
        openOnFocus
        openOnClick
        popoverPortalled={false}
        useInlineDropdown
      />
      {isLoading ? (
        <span className="pointer-events-none absolute right-3 top-0 flex h-10 items-center">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </span>
      ) : null}
    </div>
  );
}
